import { Info } from "lucide-react";

import { formatNumber } from "@/lib/format";
import { FunnelStep } from "../types/webstoreAnalytics";

interface WebstoreFunnelProps {
  funnel: FunnelStep[];
}

/**
 * The storefront funnel: viewed a page → viewed an item → added to cart → began
 * checkout → purchased.
 *
 * Bar width is share of the FIRST step, not of the previous one, so the shape of the
 * drop-off is readable at a glance. Step-over-step conversion is still shown as a
 * number next to each bar.
 *
 * The steps are not strictly sequential — a session is counted at a step if it fired
 * that event at all — so a later step can exceed an earlier one. That is flagged
 * inline rather than smoothed away, because when it happens it usually means a
 * tracking gap worth fixing.
 */
export function WebstoreFunnel({ funnel }: WebstoreFunnelProps) {
  const start = funnel[0]?.sessions ?? 0;
  const hasInversion = funnel.some(
    (step, i) => i > 0 && step.sessions > funnel[i - 1].sessions
  );

  return (
    <div className="panel p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-ink-900">Storefront funnel</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Sessions that fired each step, out of {formatNumber(start)} that viewed a page
          </div>
        </div>
        <div className="relative group/tip flex items-center shrink-0">
          <Info className="h-3.5 w-3.5 text-slate-400" />
          <div className="pointer-events-none absolute top-full right-0 mt-2 w-64 rounded-md bg-white border border-border px-3 py-2 text-[11px] leading-relaxed text-slate-600 shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
            A session counts at a step if it fired that event at any point in the visit, not
            only after the previous step. Percentages are share of the previous step.
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {funnel.map((step, index) => {
          const widthPct = start > 0 ? Math.min(100, (step.sessions / start) * 100) : 0;
          const isInverted = index > 0 && step.sessions > funnel[index - 1].sessions;

          return (
            <div key={step.event_name}>
              <div className="flex items-baseline justify-between gap-3 text-xs mb-1.5">
                <span className="font-medium text-slate-700">
                  <span className="text-slate-400 mr-1.5 num-mono">{index + 1}</span>
                  {step.label}
                </span>
                <span className="flex items-baseline gap-2 shrink-0">
                  <span className="numeric-value text-sm text-ink-900">
                    {formatNumber(step.sessions)}
                  </span>
                  {step.conversion_from_previous_pct != null && (
                    <span
                      className={`numeric-value text-[11px] ${
                        isInverted ? "text-accent-amber" : "text-slate-500"
                      }`}
                    >
                      {step.conversion_from_previous_pct}%
                    </span>
                  )}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-2.5 rounded-full ${isInverted ? "bg-accent-amber" : "bg-accent-blue"}`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                {formatNumber(step.event_count)} events · {step.conversion_from_start_pct ?? 0}% of
                step 1
              </div>
            </div>
          );
        })}
      </div>

      {hasInversion && (
        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
          A later step out-counts an earlier one. That is possible by design here, but it often
          means an earlier event is not firing on every path — worth checking against the
          storefront before reading this as a conversion story.
        </div>
      )}
    </div>
  );
}
